import { __rest, __assign } from 'tslib';
import React, { forwardRef, useState, useRef, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { Input } from '../../../../input/esm';
import { Popover } from '../../../../popover/esm';
import { getDataTestId } from '../../../../shared/esm';
import { CalendarMIcon } from '@alfalab/icons-glyph/CalendarMIcon';
import { DATE_TIME_SEPARATOR, DEFAULT_MIN_DATE, DATE_TIME_FORMAT, DATE_FORMAT, DEFAULT_MAX_DATE } from '../../consts.js';
import { isValidDate, isCompleteTime, formatDate, preventDefault, isCompleteDate, parseDateString } from '../../utils/index.js';
import { s as styles } from '../../index.module-bbbccbbb.js';
import 'date-fns/differenceInDays';
import 'date-fns/format';
import 'date-fns/isSameDay';
import 'date-fns/parse';

var DateInput = forwardRef(function (_a, ref) {
    var _b;
    var _c;
    var autoCorrection = _a.autoCorrection, open = _a.open, valueProp = _a.value, _d = _a.inputWrapperRef, inputWrapperRefProp = _d === void 0 ? null : _d, defaultValue = _a.defaultValue, _e = _a.minDate, minDate = _e === void 0 ? DEFAULT_MIN_DATE : _e, _f = _a.maxDate, maxDate = _f === void 0 ? DEFAULT_MAX_DATE : _f, picker = _a.picker, rightAddons = _a.rightAddons, Calendar = _a.Calendar, _g = _a.calendarProps, calendarProps = _g === void 0 ? {} : _g, platform = _a.platform, calendarRef = _a.calendarRef, onComplete = _a.onComplete, onChange = _a.onChange, onBlur = _a.onBlur, onCalendarClose = _a.onCalendarClose, onPickerClick = _a.onPickerClick, error = _a.error, popoverProps = _a.popoverProps, dataTestId = _a.dataTestId, wrapperClassName = _a.wrapperClassName, wrapperHandlers = _a.wrapperHandlers, block = _a.block, withTime = _a.withTime, breakpoint = _a.breakpoint, restProps = __rest(_a, ["autoCorrection", "open", "value", "inputWrapperRef", "defaultValue", "minDate", "maxDate", "picker", "rightAddons", "Calendar", "calendarProps", "platform", "calendarRef", "onComplete", "onChange", "onBlur", "onCalendarClose", "onPickerClick", "error", "popoverProps", "dataTestId", "wrapperClassName", "wrapperHandlers", "block", "withTime", "breakpoint"]);
    var _h = useState(defaultValue), value = _h[0], setValue = _h[1];
    var lastValidDate = useRef('');
    var inputRef = useRef(null);
    var inputWrapperRef = useRef(null);
    var uncontrolled = valueProp === undefined;
    var offDays = calendarProps.offDays;
    var inputValue = (_c = valueProp !== null && valueProp !== void 0 ? valueProp : value) !== null && _c !== void 0 ? _c : '';
    var _j = inputValue.split(DATE_TIME_SEPARATOR), inputDate = _j[0], inputTime = _j[1];
    var isValidValue = isValidDate({ value: inputDate, minDate: minDate, maxDate: maxDate, offDays: offDays });
    useEffect(function () {
        if (autoCorrection) {
            var hasValidValue = isValidValue && isCompleteTime(inputValue, withTime);
            if (!lastValidDate.current || !inputValue) {
                lastValidDate.current = hasValidValue
                    ? inputValue
                    : formatDate(minDate, withTime ? DATE_TIME_FORMAT : DATE_FORMAT);
            }
        }
    }, [autoCorrection, minDate, withTime, isValidValue, inputValue]);
    var getInnerError = function () {
        if (autoCorrection) {
            var isComplete = isCompleteDate(inputDate) && isCompleteTime(inputTime, withTime);
            return isComplete && !isValidValue ? 'Эта дата недоступна' : false;
        }
        return false;
    };
    var callOnComplete = function (val) {
        onComplete === null || onComplete === void 0 ? void 0 : onComplete(val, parseDateString(val, withTime ? DATE_TIME_FORMAT : DATE_FORMAT));
        lastValidDate.current = val;
    };
    var changeValue = function (val, event) {
        onChange === null || onChange === void 0 ? void 0 : onChange(event, { value: val });
        var _a = val.split(DATE_TIME_SEPARATOR), date = _a[0], _b = _a[1], time = _b === void 0 ? '' : _b;
        if (uncontrolled)
            setValue(val);
        if (isCompleteDate(date) && isCompleteTime(time, withTime))
            callOnComplete(val);
    };
    var handleChange = function (event) {
        changeValue(event.target.value, event);
    };
    var handleCalendarChange = function (date) {
        if (date) {
            changeValue(formatDate(date, withTime ? DATE_TIME_FORMAT : DATE_FORMAT), null);
            requestAnimationFrame(function () {
                var _a;
                var dateLen = DATE_FORMAT.length;
                var newCaretPos = withTime ? dateLen + DATE_TIME_SEPARATOR.length : dateLen;
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.setSelectionRange(newCaretPos, newCaretPos);
            });
        }
        if (platform === 'desktop')
            onCalendarClose === null || onCalendarClose === void 0 ? void 0 : onCalendarClose();
    };
    var handleBlur = function (event) {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
        if (autoCorrection) {
            var dateFilled = isCompleteDate(inputDate);
            if (dateFilled && !isCompleteTime(inputTime, withTime)) {
                var _a = lastValidDate.current.split(DATE_TIME_SEPARATOR), prevTime = _a[1];
                changeValue("".concat(inputDate).concat(DATE_TIME_SEPARATOR).concat(prevTime), null);
            }
            if (inputValue && !dateFilled) {
                changeValue(lastValidDate.current, null);
            }
        }
    };
    var renderCalendar = function () {
        if (picker && Calendar) {
            return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            React.createElement("div", { onMouseDown: preventDefault, className: styles.calendarWrapper },
                React.createElement(Calendar, __assign({ dataTestId: getDataTestId(dataTestId, 'calendar') }, calendarProps, { breakpoint: breakpoint, responsive: true, open: open, onClose: onCalendarClose, ref: calendarRef, value: isValidValue ? parseDateString(inputDate).getTime() : undefined, onChange: handleCalendarChange, minDate: minDate, maxDate: maxDate }))));
        }
        return null;
    };
    return (React.createElement("div", __assign({}, wrapperHandlers, { className: cn(styles.wrapper, wrapperClassName, (_b = {},
            _b[styles.block] = block,
            _b)), tabIndex: -1, "data-test-id": getDataTestId(dataTestId, 'wrapper') }),
        React.createElement(Input, __assign({}, restProps, { breakpoint: breakpoint, dataTestId: dataTestId, wrapperRef: mergeRefs([inputWrapperRef, inputWrapperRefProp]), ref: mergeRefs([ref, inputRef]), value: inputValue, inputMode: 'decimal', onInput: handleChange, onBlur: handleBlur, error: error || getInnerError(), block: true, rightAddons: React.createElement(React.Fragment, null,
                rightAddons,
                picker && (React.createElement(CalendarMIcon, { onClick: onPickerClick, className: styles.calendarIcon, onMouseDown: preventDefault }))) })),
        platform === 'desktop' ? (React.createElement(Popover, __assign({ offset: [0, 4], position: 'bottom-start', dataTestId: getDataTestId(dataTestId, 'popover') }, popoverProps, { open: open, anchorElement: inputWrapperRef.current, popperClassName: cn(styles.calendarContainer, popoverProps === null || popoverProps === void 0 ? void 0 : popoverProps.popperClassName), withTransition: false }), renderCalendar())) : (renderCalendar())));
});

export { DateInput };
